import React from 'react';
import PropTypes from 'prop-types';

import { PowerFunnelSurfaces } from '../../Analytics/constants';
import useFableticsCta from '../../hooks/useFableticsCta';
import { USER_STATUS_FREE } from '../../../utils/constants';
import { IMAGES, COPY } from '../../Fabletics/constants';
import FableticsPastDueMessage from './FableticsPastDueMessage';
import useAppleSubscriptionPopup from './useAppleSubscriptionPopup';
import styles from './fableticsVersionTwo.scss';

// `freeHeadline` is unique to this variant; the monthly + annual headlines are
// shared with FableticsSubscriptionsBanner and live in COPY.
const FREE_HEADLINE = 'POWER: Level Up Your Game, Your Travels, and Your Look.';

// PLAY (Power) wordmark — icon + bold "POWER" text inline. Same composition as
// the variant 1 banner's Power card brand row.
const PowerWordmark = () => (
    <div className={styles.brandRow}>
        <img src={IMAGES.powerIcon} alt="" className={styles.brandIcon} />
        <span className={styles.brandLabelPower}>POWER</span>
    </div>
);

const TabbedCard = ({ label, children }) => (
    <div className={styles.card}>
        <div className={styles.cardHeader}>{label}</div>
        <div className={styles.cardBody}>{children}</div>
    </div>
);

TabbedCard.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

const PlainCard = ({ children, dataTestid }) => (
    <div className={styles.cardPlain} data-testid={dataTestid}>
        {children}
    </div>
);

PlainCard.propTypes = {
    children: PropTypes.node.isRequired,
    dataTestid: PropTypes.string,
};

PlainCard.defaultProps = {
    dataTestid: null,
};

const FableticsVersionTwo = ({ surface, variant }) => {
    const { open: openAppleSubscriptionPopup, popup: appleSubscriptionPopup } = useAppleSubscriptionPopup();
    const cta = useFableticsCta({
        surface,
        variant,
        trackImpressions: true,
        onAppleManageClick: openAppleSubscriptionPopup,
    });
    const {
        buttonText,
        handleCtaClick,
        hasPastDueSubscription,
        userStatus,
        handleLearnMoreClick,
    } = cta;

    // Annual subscribers are routed to the control banner via useFabletics, so
    // this variant only ever renders for Free / Monthly / tier-null powered
    // users (the experiment audience).
    const isFree = userStatus === USER_STATUS_FREE;
    const headline = isFree ? FREE_HEADLINE : COPY.monthlyHeadline;
    const bannerClassName = isFree ? styles.banner : `${styles.banner} ${styles.bannerMonthly}`;

    return (
        <div
            className={bannerClassName}
            data-testid="fabletics.banner.version_two.container"
        >
            <p
                className={styles.headline}
                data-testid="fabletics.banner.version_two.headline"
            >
                {headline}
            </p>
            {/* Free desktop: PLAY + STAY + STYLE all in one row. Free mobile:
                cardRow becomes a flex column, so the "Included with the Annual
                Plan" subhead is rendered inline between the PLAY card and the
                STAY/STYLE pair (hidden on desktop). Monthly state hides the
                PLAY card and the subhead since Power is already owned. */}
            <div className={styles.cardRow}>
                {isFree && (
                    <TabbedCard label="PLAY">
                        <PowerWordmark />
                        <p className={styles.cardCopy}>{COPY.powerCard}</p>
                    </TabbedCard>
                )}
                {isFree && (
                    <p
                        className={styles.includedSubhead}
                        data-testid="fabletics.banner.version_two.included-subhead"
                    >
                        Included with the Annual Plan
                    </p>
                )}
                {isFree ? (
                    <TabbedCard label="STAY">
                        <img
                            src={IMAGES.gpLogo}
                            alt="Global Privileges"
                            className={`${styles.brandLogo} ${styles.brandLogoGp}`}
                            data-testid="fabletics.banner.version_two.gp-card.logo"
                        />
                        <p className={styles.cardCopy}>{COPY.gpCard}</p>
                    </TabbedCard>
                ) : (
                    <PlainCard dataTestid="fabletics.banner.version_two.gp-card">
                        <img
                            src={IMAGES.gpLogo}
                            alt="Global Privileges"
                            className={`${styles.brandLogo} ${styles.brandLogoGp}`}
                            data-testid="fabletics.banner.version_two.gp-card.logo"
                        />
                        <p className={styles.cardCopy}>{COPY.gpCard}</p>
                    </PlainCard>
                )}
                {isFree ? (
                    <TabbedCard label="STYLE">
                        <img
                            src={IMAGES.fableticsLogo}
                            alt="Fabletics"
                            className={styles.brandLogo}
                            data-testid="fabletics.banner.version_two.fabletics-card.logo"
                        />
                        <p className={`${styles.cardCopy} ${styles.cardCopyFabletics}`}>{COPY.fableticsCard}</p>
                    </TabbedCard>
                ) : (
                    <PlainCard dataTestid="fabletics.banner.version_two.fabletics-card">
                        <img
                            src={IMAGES.fableticsLogo}
                            alt="Fabletics"
                            className={styles.brandLogo}
                            data-testid="fabletics.banner.version_two.fabletics-card.logo"
                        />
                        <p className={`${styles.cardCopy} ${styles.cardCopyFabletics}`}>{COPY.fableticsCard}</p>
                    </PlainCard>
                )}
            </div>
            <div className={styles.ctaSection}>
                <FableticsPastDueMessage
                    hasPastDueSubscription={hasPastDueSubscription}
                    className={styles.pastDueMessage}
                    testId="fabletics.banner.version_two.past-due-message"
                />
                <button
                    type="button"
                    className={styles.primaryCta}
                    data-testid="fabletics.banner.version_two.primary-cta"
                    onClick={handleCtaClick}
                >
                    {buttonText}
                </button>
                <button
                    type="button"
                    className={styles.learnMore}
                    data-testid="fabletics.banner.version_two.learn-more"
                    onClick={handleLearnMoreClick}
                >
                    Learn more
                </button>
            </div>
            {appleSubscriptionPopup}
        </div>
    );
};

FableticsVersionTwo.propTypes = {
    surface: PropTypes.oneOf([PowerFunnelSurfaces.HOME, PowerFunnelSurfaces.POWER_PERKS_PAGE]),
    variant: PropTypes.string,
};

FableticsVersionTwo.defaultProps = {
    surface: PowerFunnelSurfaces.HOME,
    variant: 'version_two',
};

export default FableticsVersionTwo;
